/**
 * Metered exports.
 *
 * Exports go through the server rather than being built in the browser, so
 * the monthly row allowance can be counted and a customer can be told what
 * is left. The rows posted are the rows already on screen: the server counts
 * what it receives, so the meter reflects reality.
 *
 * exportCsv in utils stays for the admin upload-error export, which reports
 * on an admin's own file rather than sending market data out of the product.
 */

import { API_BASE } from './config';
import { readAuth, apiFetch } from './apiClient';

function authHeaders() {
  const token = readAuth()?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** What this account has exported this month, and what it may still. */
export const getExportUsage = () => apiFetch('/api/market/export/usage');

/**
 * Ask the server for a file and hand it to the browser.
 * Throws an Error carrying the server's message (and .status) on refusal,
 * so a plan ceiling can be told apart from a genuine failure.
 */
export async function requestExport(filename, headers, rows, format = 'csv') {
  const res = await fetch(`${API_BASE}/api/market/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ filename, format, headers, rows }),
  });

  if (!res.ok) {
    let detail;
    try {
      detail = (await res.json()).detail;
    } catch {
      /* non-JSON error body */
    }
    const err = new Error(detail || `Export failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = match ? match[1] : `${filename}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return { rows: Number(res.headers.get('X-Export-Rows') || rows.length) };
}
